import { tallyTokens } from "../../theme.tsx";
import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SETTINGS from "../../lib/TallyLibrary/constants/configGeneral.json";
import { Trans } from "@lingui/react/macro";

interface TallyDialogProps {
  open: boolean;
  title: string;
  subtitle: string;
  submitTitle: string;
  initialInputValue?: string;
  onSubmit: (inputValue: string) => void;
  handleDialogClose: () => void;
}

const TallyDialog: React.FC<TallyDialogProps> = ({
  open,
  title,
  subtitle,
  submitTitle,
  onSubmit,
  initialInputValue = "",
  handleDialogClose,
}) => {
  const theme = useTheme();
  const [inputValue, setInputValue] = React.useState(initialInputValue);

  React.useEffect(() => {
    if (open) {
      setInputValue(initialInputValue);
    }
  }, [initialInputValue, open]);

  // Handle input change
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  // Handle form submission
  const handleSubmit = () => {
    onSubmit(inputValue);
    setInputValue("");
  };

  const handleClose = () => {
    handleDialogClose();
    setInputValue("");
  };

  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        PaperProps={{
          component: "form",
          onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
          },
          sx: {
            borderRadius: theme.spacing(2),
            backgroundColor: tallyTokens.surface,
            border: `1px solid ${tallyTokens.line}`,
            boxShadow: tallyTokens.shadow.lg,
            marginLeft: useMediaQuery(theme.breakpoints.up("md"))
              ? `calc(${SETTINGS.drawerWidth}px)`
              : "auto",
            paddingBottom: theme.spacing(2.5),
          },
        }}
      >
        <DialogContent>
          <DialogTitle
            sx={{
              color: tallyTokens.brandInk,
              paddingLeft: 0,
            }}
          >
            {title}
          </DialogTitle>
          <DialogContentText
            sx={{
              color: tallyTokens.ink2,
              paddingBottom: theme.spacing(1),
            }}
          >
            {subtitle}
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            variant="filled"
            value={inputValue}
            multiline
            rows={5}
            onChange={handleInputChange}
            InputProps={{
              style: {
                borderRadius: theme.spacing(2),
                paddingBottom: theme.spacing(1),
              },
              disableUnderline: true,
            }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            marginRight: theme.spacing(2),
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              borderRadius: theme.spacing(2),
            }}
          >
            <Trans
              context={
                "Button title of a dialog which allows the user to cancel the action intended with the opening of the dialog."
              }
            >
              Cancel
            </Trans>
          </Button>
          <Button
            variant="contained"
            sx={{
              borderRadius: theme.spacing(2),
            }}
            onClick={handleSubmit}
          >
            {submitTitle}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default TallyDialog;
